/**
 * Block 12 PR-B Phase B3 Part C Step 18 + Phase B4 Step 3 — SectionAdditional (В).
 *
 * Renders rows из `methodology.applicableAdditional[]` (intersection с domain
 * `CalculatorAdditionalSchema` enum). Each row toggleable через `ToggleableRow`.
 *
 * Russian labels — fixed map ниже (matches CalculatorAdditional enum). Anketa
 * methodology controls **which** ids appear (Kitchen vs Cabinet и т.д.); все
 * labels общие.
 *
 * **Phase B4 Step 3 (closes OQ-4):** для `id === 'facade-radius'` под toggle
 * рендерится inline `<RadioCard>` с 5 типами radius фасада (методика §2.1 Б.2).
 * Selection хранится в `state.additionalSelections['facade-radius']`. Validation
 * (anketaReducer) — toggle on AND no selection → FieldError на path
 * `additionalSelections.facade-radius`, выводится через `errorForPath`.
 *
 * **Hotfix-2B Phase B4 Steps 8-10 (Bug 12):** для `id === 'aluminum-glass-vitrine'`
 * рендерится `<VitrineCompound>` (compound L/H/pricePerM2 + auto-area), не
 * generic `<ToggleableRow>`. Применяется ко всем anketas в чьём
 * `applicableAdditional` есть `aluminum-glass-vitrine`: Kitchen (2.12.2),
 * Cabinet (2.12.3, heading «Особые конструкции» per SECTION_ADDITIONAL_HEADER),
 * BathTabletop (2.12.4), TV-зона (2.12.7 — также получает `vitrineHint` когда
 * `hasShelf=true`).
 *
 * **Hotfix-2C Phase C3 Step 6 (2026-05-17 — Cabinet revert):** Phase B5 Step 12
 * отменён, Cabinet `applicableAdditional` снова `['facade-radius', 'aluminum-glass-vitrine']`
 * per Schemas v2 line 97-99. Section В для Cabinet содержит facade-radius
 * (FacadeRadiusBlock) + Витрину (VitrineCompound) под заголовком «Особые
 * конструкции». SectionFilling для Cabinet больше не рендерит leading
 * FacadeRadiusBlock.
 *
 * Reference: ADR-0020 §Q1 (applicableAdditional), методика §1.4.2 + §2.1 Б.2;
 * Schemas v2 (2026-05-17) «Шкаф / Особые конструкции» line 97-99; прототип
 * 2.12.3:462-486 + 2.12.2:459-486.
 */

import type { CalculatorAnketaType, CalculatorMethodology } from '@pomoshnik/shared';
import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { SECTION_ADDITIONAL_HEADER } from '../anketaTypeLabels';
import { ADDITIONAL_META, additionalDeeplink } from '../additionalMeta';
import { ToggleableRow } from './ToggleableRow';
import { VitrineCompound } from '../components/VitrineCompound';
import { FACADE_RADIUS_ROW_ID, FacadeRadiusBlock } from '../components/FacadeRadiusBlock';

const VITRINE_ROW_ID = 'aluminum-glass-vitrine';

export interface SectionAdditionalProps {
  /** Anketa type (для per-anketa heading: Cabinet → «Особые конструкции»). */
  anketaType: CalculatorAnketaType;
  applicableAdditional: CalculatorMethodology['applicableAdditional'];
  toggles: Record<string, boolean>;
  quantities: Record<string, number>;
  prices: Record<string, number>;
  selections: Record<string, string>;
  onToggle: (id: string, on: boolean) => void;
  onQty: (id: string, qty: number) => void;
  onPrice: (id: string, price: number) => void;
  onSelection: (id: string, value: string) => void;
  /** Container-provided lookup. Returns undefined if no error для path. */
  errorForPath?: (path: string) => string | undefined;
  /**
   * Hotfix-2B Phase B4 Step 9 — Витрина compound dimensions (Bug 12).
   * Используется только для row.id === 'aluminum-glass-vitrine'; обычные
   * additional ids ignor'ят эти maps.
   */
  lengths?: Record<string, number>;
  heights?: Record<string, number>;
  pricesPerM2?: Record<string, number>;
  onLength?: (id: string, length: number) => void;
  onHeight?: (id: string, height: number) => void;
  onPricePerM2?: (id: string, pricePerM2: number) => void;
  /**
   * Optional hint для Витрина row. TV-зона передаёт warning когда hasShelf=true
   * и vitrineOn=true (фасад шкафа считается только по двум глубинам).
   */
  vitrineHint?: string;
  /**
   * PR-calc-buttons-unify (ADR-0099, ADR-0100) — жёлтая кнопка «Найти и узнать цену».
   * Container делает per-id lookup (additionalDeeplink): facade-radius/vitrine →
   * /partners, остальные → /furniture.
   */
  onDeeplink?: (rowId: string) => void | Promise<void>;
  /**
   * PR-H1 Round 3 — per-row brand input для Доп.элементов (Section В). Прокидывает
   * brand value + onBrandChange в VitrineCompound / FacadeRadiusBlock / generic
   * ToggleableRow. Render gated на `onBrand` — без него brand input скрыт.
   */
  brandSelections?: Record<string, string>;
  onBrand?: (id: string, brand: string) => void;
}

export function SectionAdditional({
  anketaType,
  applicableAdditional,
  toggles,
  quantities,
  prices,
  selections,
  onToggle,
  onQty,
  onPrice,
  onSelection,
  errorForPath,
  lengths,
  heights,
  pricesPerM2,
  onLength,
  onHeight,
  onPricePerM2,
  vitrineHint,
  onDeeplink,
  brandSelections,
  onBrand,
}: SectionAdditionalProps) {
  const brandFor = (id: string): string | undefined =>
    typeof brandSelections?.[id] === 'string' ? brandSelections[id] : undefined;
  const onBrandFor = (id: string): ((brand: string) => void) | undefined =>
    onBrand ? (b) => onBrand(id, b) : undefined;
  if (applicableAdditional.length === 0) return null;

  const heading = SECTION_ADDITIONAL_HEADER[anketaType];

  return (
    <SectionCard testId="section-additional">
      <SectionHeader>{heading}</SectionHeader>
      <div className="flex flex-col gap-3">
        {applicableAdditional.map((id) => {
          const meta = ADDITIONAL_META[id];
          if (!meta) return null;
          const enabled = Boolean(toggles[id]);

          // Phase B4 Step 9 — special-case Витрина (compound rendering).
          if (id === VITRINE_ROW_ID) {
            return (
              <div key={id} className="flex flex-col gap-2">
                <VitrineCompound
                  enabled={enabled}
                  length={lengths?.[id] ?? 0}
                  height={heights?.[id] ?? 0}
                  pricePerM2={pricesPerM2?.[id] ?? 0}
                  onChange={(patch) => {
                    if (patch.enabled !== undefined) onToggle(id, patch.enabled);
                    if (patch.length !== undefined) onLength?.(id, patch.length);
                    if (patch.height !== undefined) onHeight?.(id, patch.height);
                    if (patch.pricePerM2 !== undefined) onPricePerM2?.(id, patch.pricePerM2);
                  }}
                  hint={enabled ? vitrineHint : undefined}
                  /* PR-calc-buttons-unify (ADR-0099) — жёлтая кнопка → /partners. */
                  onDeeplink={onDeeplink ? () => onDeeplink(id) : undefined}
                  /* PR-H1 Round 3 — per-row brand. */
                  brand={brandFor(id)}
                  onBrandChange={onBrandFor(id)}
                />
              </div>
            );
          }

          // Phase B5 Step 12 — facade-radius rendered через `<FacadeRadiusBlock>`
          // primitive (extracted Phase B5). Тот же UX что был inline в Phase B4
          // Step 3: ToggleableRow + DeeplinkButton(partner) + RadioCard sub-picker.
          if (id === FACADE_RADIUS_ROW_ID) {
            return (
              <FacadeRadiusBlock
                key={id}
                enabled={enabled}
                qty={quantities[id] ?? 0}
                price={prices[id] ?? 0}
                selection={selections[id]}
                onToggle={(on) => onToggle(id, on)}
                onQty={(q) => onQty(id, q)}
                onPrice={(p) => onPrice(id, p)}
                onSelection={(value) => onSelection(id, value)}
                errorForPath={errorForPath}
                /* PR-calc-buttons-unify (ADR-0099) — жёлтая кнопка → /partners. */
                onDeeplink={onDeeplink ? () => onDeeplink(id) : undefined}
                /* PR-H1 Round 3 — per-row brand. */
                brand={brandFor(id)}
                onBrandChange={onBrandFor(id)}
              />
            );
          }

          return (
            <ToggleableRow
              key={id}
              id={id}
              label={meta.label}
              unit={meta.unit}
              enabled={enabled}
              qty={quantities[id] ?? 0}
              price={prices[id] ?? 0}
              onToggle={(on) => onToggle(id, on)}
              onQty={(q) => onQty(id, q)}
              onPrice={(p) => onPrice(id, p)}
              /* PR-calc-buttons-unify (ADR-0099) — жёлтая «Найти и узнать цену»;
                 target per-id (partner/furniture) определяет Container. */
              deeplink={additionalDeeplink(id)}
              onDeeplink={onDeeplink ? () => onDeeplink(id) : undefined}
              /* PR-H1 Round 3 — per-row brand. */
              brand={brandFor(id)}
              onBrandChange={onBrandFor(id)}
            />
          );
        })}
      </div>
    </SectionCard>
  );
}
